'use client';

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '@prismo-io/design-system';
import { useMe } from '@prismo-io/feature-auth/providers/me';
import {
  AuraProfileCode,
  type AuraProfileCodeT,
  LanguageCode,
  type LanguageCodeT,
  type UsersT,
} from '@prismo-io/schemas';
import Image from 'next/image';
import { type FC, useCallback, useMemo } from 'react';

import AdvisorIllustration from '@/assets/aura/app/advisor/common/report.svg';
import AnalystIllustration from '@/assets/aura/app/analyst/common/report.svg';
import AnimatorIllustration from '@/assets/aura/app/animator/common/report.svg';
import CoachIllustration from '@/assets/aura/app/coach/common/report.svg';
import PeacemakerIllustration from '@/assets/aura/app/peacemaker/common/report.svg';
import PilotIllustration from '@/assets/aura/app/pilot/common/report.svg';
import PlannerIllustration from '@/assets/aura/app/planner/common/report.svg';
import DefaultIllustration from '@/assets/aura/app/primary/common/report.svg';
import ProtectorIllustration from '@/assets/aura/app/protector/common/report.svg';
import { TypeSchema, useDownloadPDFUser } from '@prismo-io/feature-pdf';

type Props = {
  order: number;
  user: Pick<UsersT, 'id' | 'full_name' | 'has_aura' | 'has_helio'>;
  profileType?: AuraProfileCodeT;
  userUrl: string;
  hasCV: boolean;
  expertAccess: boolean;
};

export const CVCard: FC<Props> = (props: Props) => {
  const { profileType, order, user, userUrl, hasCV, expertAccess } = props;

  const me = useMe();

  const isMe = me && me.url === userUrl;

  const img = useMemo(() => {
    if (!hasCV) {
      return DefaultIllustration;
    }
    switch (profileType) {
      case AuraProfileCode.Enum.ADVISOR:
        return AdvisorIllustration;
      case AuraProfileCode.Enum.ANALYST:
        return AnalystIllustration;
      case AuraProfileCode.Enum.ANIMATOR:
        return AnimatorIllustration;
      case AuraProfileCode.Enum.COACH:
        return CoachIllustration;
      case AuraProfileCode.Enum.PEACEMAKER:
        return PeacemakerIllustration;
      case AuraProfileCode.Enum.PILOT:
        return PilotIllustration;
      case AuraProfileCode.Enum.PLANNER:
        return PlannerIllustration;
      case AuraProfileCode.Enum.PROTECTOR:
        return ProtectorIllustration;
      default:
        return DefaultIllustration;
    }
  }, [profileType, hasCV]);

  const { i18n } = useLingui();
  const { locale = LanguageCode.Enum.fr } = i18n;

  const downloadCV = useDownloadPDFUser();

  const onDownload = useCallback(() => {
    if (!user) {
      return;
    }

    downloadCV({
      locale: locale as LanguageCodeT,
      users: [user],
      type: TypeSchema.Enum.CV,
    });
  }, [locale, user]);

  return (
    <Stack
      component="article"
      bgcolor={hasCV ? 'background.primary' : 'white'}
      borderRadius="3rem"
      p={2}
      minWidth={{ none: '100%', sm: '45%', lg: '31%' }}
      sx={{
        boxShadow: '2px 2px 7px 0px rgba(0,0,0,0.3)',
        flex: '1 1 0%',
        order: order,
      }}
      direction="column"
      alignItems="center"
      spacing={2}
    >
      <Box component="div" position="relative" width="10.5rem" height="10.5rem">
        <Image
          src={img}
          alt={t`Profil Adapté Aura | PRISMO`}
          style={{ objectFit: 'contain', objectPosition: 'center' }}
          fill
        />
      </Box>
      <Box component="div">
        <Button
          disabled={!hasCV || (!isMe && !expertAccess)}
          variant="contained"
          color="gradient"
          sx={{
            '& .MuiButton-startIcon': {
              display: 'none',
            },
          }}
          onClick={onDownload}
        >
          <Trans>Téléchargez</Trans>
        </Button>
      </Box>
      <Stack direction="row" gap="3px">
        <Typography variant="h3">
          {hasCV && isMe && <Trans>Téléchargez votre CV PRISMO</Trans>}
          {hasCV && !isMe && (
            <Trans>Téléchargez le CV PRISMO de cet utilisateur</Trans>
          )}
          {!hasCV && isMe && (
            <Trans>Votre CV PRISMO n'est pas encore disponible</Trans>
          )}
          {!hasCV && !isMe && (
            <Trans>
              Le CV PRISMO de cet utilisateur n'est pas encore disponible
            </Trans>
          )}
        </Typography>
      </Stack>
    </Stack>
  );
};
